// #5692 — pure rules for the native image cache (native-image-cache.service.ts).
// Kept free of Angular and Capacitor imports so they are unit-testable under
// mocha/ts-node (native-image-cache-rules.test.ts), the same split as
// image-upload-outbox-rules.ts. Design: design-docs/image-uploads.md,
// "Native image cache (#5692)".

import { ScaledImageSize } from '@cf-shared/data-model/scaled-image';
import { ImageSize } from '@cf-shared/enums';

// the cache stays below both limits; the least recently shown files go first
export const NATIVE_IMAGE_CACHE_MAX_BYTES = 100 * 1024 * 1024;
export const NATIVE_IMAGE_CACHE_MAX_FILES = 2000;

export const NATIVE_IMAGE_CACHE_INDEX_VERSION = 1;

/** One cached image: the file holding its bytes, their size, and when it was last shown. */
export interface NativeImageCacheEntry {
  file: string;
  bytes: number;
  lastUsedMs: number;
}

/** The index file's shape on disk. Keyed by the image's public URL. */
export interface NativeImageCacheIndexFile {
  version: number;
  entries: Record<string, NativeImageCacheEntry>;
}

// the two hosts user images are served from: every current download URL, and
// legacy URLs still stored in older documents. the same pair the web service
// worker caches (client/templates/sw.js)
const CACHEABLE_URL_PREFIXES = [
  'https://storage.googleapis.com/',
  'https://firebasestorage.googleapis.com/',
];

/** Whether this image URL is one the cache keeps. Anything else (a blob: preview, a file the app
 * ships, some other host) is shown as it is. */
export function isCacheableImageUrl(url: string | undefined | null): url is string {
  return typeof url === 'string' && CACHEABLE_URL_PREFIXES.some(prefix => url.startsWith(prefix));
}

// a cache file name is the SHA-256 of the URL in hex plus an extension. anything else read back
// from the index is refused, so a damaged index can never point the app at a file outside the
// cache folder
const CACHE_FILE_NAME = /^[0-9a-f]{64}\.(jpg|jpeg|png|webp|gif|heic|img)$/;
const KNOWN_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic'];

/** The file name for an image, from the hex SHA-256 of its URL and the URL's own extension. The
 * extension only helps the web view pick a decoder; an unknown one becomes `img`. */
export function cacheFileNameFor(urlSha256Hex: string, url: string): string {
  // the last path segment, without any query or fragment
  const path = url.split(/[?#]/)[0];
  const lastSegment = path.slice(path.lastIndexOf('/') + 1);
  const dot = lastSegment.lastIndexOf('.');
  const candidate = dot >= 0 ? lastSegment.slice(dot + 1).toLowerCase() : '';
  const extension = KNOWN_EXTENSIONS.includes(candidate) ? candidate : 'img';
  return `${urlSha256Hex}.${extension}`;
}

/** Whether this file name could be one the cache wrote. The folder listing is checked against it
 * so that a repair never deletes something the cache did not create. */
export function isCacheFileName(name: string): boolean {
  return CACHE_FILE_NAME.test(name);
}

/**
 * Whether a Filesystem plugin error means the file or folder is not there. Both the iOS and the
 * Android plugin word that case as "... does not exist." (FilesystemError.swift, FilesystemErrors.kt
 * in @capacitor/filesystem 8). Any other failure is a real one and must not be read as "missing".
 */
export function isFileMissingError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : (err as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message.includes('does not exist');
}

/**
 * Whether a Filesystem plugin error from `mkdir` means the folder is already there. Both the iOS
 * and the Android plugin word that case as "Directory at '...' already exists, cannot be
 * overwritten." (FilesystemError.swift, FilesystemErrors.kt in @capacitor/filesystem 8). For the
 * cache that is success: the folder it asked for exists.
 */
export function isFolderAlreadyExistsError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : (err as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message.includes('already exists');
}

/**
 * Read the index file's parsed JSON back into entries, repairing whatever is wrong with it.
 *
 * The file may have been written by an older app version, cut short by a crash, or damaged. A row
 * that is not a usable entry is dropped, never trusted. `damaged` says whether anything had to be
 * dropped or the whole file was unusable, so the caller can report it and rewrite the file.
 */
export function parseCacheIndex(raw: unknown): { entries: Map<string, NativeImageCacheEntry>; damaged: boolean } {
  const entries = new Map<string, NativeImageCacheEntry>();
  if (!raw || typeof raw !== 'object') {
    return { entries, damaged: true };
  }
  const file = raw as Partial<NativeImageCacheIndexFile>;
  if (file.version !== NATIVE_IMAGE_CACHE_INDEX_VERSION || !file.entries || typeof file.entries !== 'object') {
    return { entries, damaged: true };
  }
  let damaged = false;
  // for each row: keep it only if every field has the right type and the file name is ours
  for (const [url, row] of Object.entries(file.entries)) {
    const entry = row as Partial<NativeImageCacheEntry> | null;
    const valid = isCacheableImageUrl(url)
      && !!entry && typeof entry === 'object'
      && typeof entry.file === 'string' && CACHE_FILE_NAME.test(entry.file)
      && typeof entry.bytes === 'number' && Number.isFinite(entry.bytes) && entry.bytes >= 0
      && typeof entry.lastUsedMs === 'number' && Number.isFinite(entry.lastUsedMs);
    if (valid) {
      entries.set(url, { file: entry.file, bytes: entry.bytes, lastUsedMs: entry.lastUsedMs });
    } else {
      damaged = true;
    }
  }
  return { entries, damaged };
}

/** The epoch some piece of work began under, from `CacheEpoch.begin()`. */
export type CacheEpochToken = number & { readonly __cacheEpochToken: unique symbol };

/**
 * Which clear of the cache we are past. Every clear (sign-out, "Delete Private Data") moves it on,
 * and work that began under an older epoch must not store its result: the clear has already
 * deleted the folder that result belongs in. The service's one save path asks `isCurrent` right
 * before it starts a write, so no caller has to remember to.
 */
export class CacheEpoch {
  private value = 0;

  /** a token for work starting now */
  begin(): CacheEpochToken {
    return this.value as CacheEpochToken;
  }

  /** whether no clear has happened since this token was handed out */
  isCurrent(token: CacheEpochToken): boolean {
    return token === this.value;
  }

  /** a clear is starting: every token handed out so far stops being current */
  advance(): void {
    this.value++;
  }
}

/** The index file's JSON text for these entries. */
export function serializeCacheIndex(entries: Map<string, NativeImageCacheEntry>): string {
  const file: NativeImageCacheIndexFile = {
    version: NATIVE_IMAGE_CACHE_INDEX_VERSION,
    entries: Object.fromEntries(entries),
  };
  return JSON.stringify(file);
}

/**
 * The URLs to remove so the cache fits both limits, least recently shown first.
 */
export function selectEvictions(
  entries: Map<string, NativeImageCacheEntry>,
  limits: { maxBytes: number; maxFiles: number } = {
    maxBytes: NATIVE_IMAGE_CACHE_MAX_BYTES,
    maxFiles: NATIVE_IMAGE_CACHE_MAX_FILES,
  },
): string[] {
  let totalBytes = 0;
  for (const entry of entries.values()) {
    totalBytes += entry.bytes;
  }
  let fileCount = entries.size;
  const oldestFirst = [...entries.entries()].sort((a, b) => a[1].lastUsedMs - b[1].lastUsedMs);
  const evicted: string[] = [];
  // for each entry, oldest first: remove it while either limit is still exceeded
  for (const [url, entry] of oldestFirst) {
    if (totalBytes <= limits.maxBytes && fileCount <= limits.maxFiles) {
      break;
    }
    evicted.push(url);
    totalBytes -= entry.bytes;
    fileCount--;
  }
  return evicted;
}

/** The pixel density an image of this width is offered at in a density `srcset`. ImageService's
 * `getSrcSetByDpi` uses this too, so the cache keeps the size the web view would pick. */
export function densityForWidth(width: number): number {
  if (width > ImageSize.Medium) {
    return 3;
  }
  if (width > ImageSize.Small) {
    return 2;
  }
  return 1;
}

/** Whether this is the square thumbnail size the food cards show. */
export function isThumbnailSize(size: ScaledImageSize): boolean {
  return size.width === ImageSize.Thumbnail && size.height === ImageSize.Thumbnail;
}

/**
 * The one larger size of a photo to keep on the device: the size a density `srcset` would show on
 * a screen with this `devicePixelRatio`. That is the smallest density at or above the screen's, or
 * the highest one on offer when none reaches it. A photo with only a thumbnail answers with that.
 */
export function pickDisplayVariant(
  sizes: ScaledImageSize[] | undefined | null,
  devicePixelRatio: number,
): ScaledImageSize | undefined {
  const all = sizes ?? [];
  const larger = all.filter(size => !isThumbnailSize(size));
  if (larger.length === 0) {
    return all.find(isThumbnailSize);
  }
  const byDensity = [...larger].sort((a, b) => densityForWidth(a.width) - densityForWidth(b.width));
  return byDensity.find(size => densityForWidth(size.width) >= devicePixelRatio) ?? byDensity[byDensity.length - 1];
}
